/**
 * Rewrites downstream expression references when an action is renamed.
 *
 * Logic Apps actions reference each other's output by name inside expression
 * strings — e.g. `@outputs('Old_Action')`, `@{body('Old_Action')?.['id']}`, or
 * `items('For_each')`. Renaming an action only changes its JSON key and
 * `runAfter` links; without also rewriting these expression references every
 * downstream consumer would keep pointing at the now-missing old name.
 *
 * The reference functions below all take the referenced action/scope name as a
 * single- or double-quoted first argument. We rewrite only those call sites, so
 * an action name appearing elsewhere (a dereference like `outputs('X')?.['Old_Action']`,
 * or free text) is left untouched.
 */

/**
 * Expression functions whose first argument is an action or scope name.
 * Matches Logic Apps workflow definition language semantics.
 */
const ACTION_REFERENCE_FUNCTIONS = [
  'outputs',
  'body',
  'actionOutputs',
  'actionBody',
  'actions',
  'result',
  'items',
  'iterationIndexes',
] as const;

/** Pre-joined alternation of reference function names (constant across calls). */
const REFERENCE_FUNCS_ALTERNATION = ACTION_REFERENCE_FUNCTIONS.join('|');

/** Escape a string for safe interpolation into a RegExp. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build the reference-rewrite pattern for a given action name. The leading `\b`
 * word boundary ensures we match a whole function token and never the tail of a
 * longer identifier (e.g. a token ending in "outputs") — the reference functions
 * all start with a word character, so `\b` before them means "preceded by a
 * non-word character or start of string". We use `\b` rather than a lookbehind
 * because the build targets Safari 14, which does not support regex lookbehind.
 * The first captured group is the function name + opening paren + optional
 * whitespace; the second group captures the quote character so replacing only
 * the quoted name preserves the call site's quote style.
 */
function buildReferencePattern(oldName: string): RegExp {
  return new RegExp(
    `\\b((?:${REFERENCE_FUNCS_ALTERNATION})\\s*\\(\\s*)(['"])${escapeRegExp(oldName)}\\2`,
    'g',
  );
}

/**
 * Apply a precompiled reference pattern to a single string. Reusing a global
 * (`/g`) regex across `String.replace` calls is safe: replace resets the regex's
 * `lastIndex` on completion, so no state leaks between strings.
 */
function applyReferencePattern(value: string, pattern: RegExp, newName: string): string {
  return value.replace(
    pattern,
    (_match, prefix: string, quote: string) => `${prefix}${quote}${newName}${quote}`,
  );
}

/** Recursively rewrite reference strings using a precompiled pattern. */
function rewriteReferences<T>(node: T, pattern: RegExp, newName: string): T {
  if (typeof node === 'string') {
    return applyReferencePattern(node, pattern, newName) as unknown as T;
  }
  if (Array.isArray(node)) {
    return node.map((item) => rewriteReferences(item, pattern, newName)) as unknown as T;
  }
  if (node !== null && typeof node === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node)) {
      const rewritten = rewriteReferences(value, pattern, newName);
      // Assign as a plain data property. Bracket assignment of `__proto__`
      // would trigger the prototype setter (prototype pollution), so route it
      // through defineProperty to keep every key a data property.
      if (key === '__proto__') {
        Object.defineProperty(result, key, {
          value: rewritten,
          enumerable: true,
          configurable: true,
          writable: true,
        });
      } else {
        result[key] = rewritten;
      }
    }
    return result as T;
  }
  return node;
}

/**
 * Replace `func('oldName')` or `func("oldName")` references with the same quote
 * style inside a single expression string. Only the reference functions above
 * are rewritten, and the name must match exactly between the quotes so `Get`
 * never matches `Get_Users`.
 */
export function updateActionReferencesInString(
  value: string,
  oldName: string,
  newName: string,
): string {
  return applyReferencePattern(value, buildReferencePattern(oldName), newName);
}

/**
 * Deep-clone a workflow action tree (or any JSON value), rewriting every
 * expression-reference string from `oldName` to `newName`.
 *
 * Only string *values* are rewritten — object keys (action names, `runAfter`
 * entries) are preserved, since those are handled by the caller's key rename.
 * Returns a new structure; the input is not mutated. The reference pattern is
 * compiled once and reused across the whole walk.
 */
export function updateActionReferences<T>(node: T, oldName: string, newName: string): T {
  return rewriteReferences(node, buildReferencePattern(oldName), newName);
}
